import React, { useEffect, useState } from "react"
import { Card, CardBody, Table, Input, Row, Col } from "reactstrap"
import PropTypes from "prop-types"
import { useNavigate, useSearchParams } from "react-router-dom"
import { FILES_URL } from "helpers/url_helper"
import stickyTHeadStyle from "constants/stickyTHeadStyle"

const LocationsTable = ({ title, lots }) => {
    const [searchParams, setSearchParams] = useSearchParams()

    const [search, setSearch] = useState(searchParams.get("search") || "")
    const [filterSupervisor, setFilterSupervisor] = useState(searchParams.get("supervisor") || "All Managers")
    const [filterType, setFilterType] = useState(searchParams.get("type") || "All Types")
    const [filterCurbAds, setFilterCurbAds] = useState(searchParams.get("curbads") || "CurbAds Filter")
    const navigate = useNavigate()

    useEffect(() => {
        const params = {}
        if (search) params.search = search
        if (filterSupervisor !== "All Managers") params.supervisor = filterSupervisor
        if (filterType !== "All Types") params.type = filterType
        if (filterCurbAds !== "CurbAds Filter") params.curbads = filterCurbAds
        setSearchParams(params)
    }, [search, filterSupervisor, filterType, filterCurbAds])

    const supervisors = ["All Managers", ...new Set(lots.map(l => l.account_manager?.full_name).filter(Boolean))]

    const filtered = lots.filter(lot => {
        const matchesSearch =
            lot.location_name.toLowerCase().includes(search.toLowerCase()) ||
            lot.account_manager?.full_name?.toLowerCase().includes(search.toLowerCase())

        const matchesSupervisor =
            filterSupervisor === "All Managers" || lot.account_manager?.full_name === filterSupervisor

        const matchesType =
            filterType === "All Types" || lot.operating_structure === filterType

        const matchesCurbAds =
            filterCurbAds === "CurbAds Filter" ||
            (filterCurbAds === "Yes" && lot.is_curbads_location == 1) ||
            (filterCurbAds === "No" && lot.is_curbads_location != 1)

        return matchesSearch && matchesSupervisor && matchesType && matchesCurbAds
    })

    const handleRowClick = lotId => {
        const currentParams = searchParams.toString();
        navigate(currentParams ? `/locations/${lotId}?${currentParams}` : `/locations/${lotId}`);
    };

    return (
        <Col xl={12}>
            <Card className="accordion-gradient-border" style={{ background: "#3C3C3C" }}>
                <CardBody>
                    {title && <h5 className="mb-3">{title}</h5>}

                    <Row className="mb-3 d-flex justify-content-between align-items-center">
                        <Col md="auto">
                            <Input
                                type="text"
                                placeholder="Search"
                                className="accordion-gradient-border"
                                value={search}
                                onChange={e => setSearch(e.target.value)}
                            />
                        </Col>

                        <Col md="auto" className="d-flex gap-2">
                            <select
                                className="form-select form-select-sm w-auto accordion-gradient-border"
                                value={filterSupervisor}
                                onChange={e => setFilterSupervisor(e.target.value)}
                            >
                                {supervisors.map((s, idx) => (
                                    <option key={idx}>{s}</option>
                                ))}
                            </select>

                            <select
                                className="form-select form-select-sm w-auto accordion-gradient-border"
                                value={filterType}
                                onChange={e => setFilterType(e.target.value)}
                            >
                                <option>All Types</option>
                                <option>Lease</option>
                                <option>Non Lease</option>
                            </select>

                            <select
                                className="form-select form-select-sm w-auto accordion-gradient-border"
                                value={filterCurbAds}
                                onChange={e => setFilterCurbAds(e.target.value)}
                            >
                                <option>CurbAds Filter</option>
                                <option>Yes</option>
                                <option>No</option>
                            </select>
                        </Col>
                    </Row>

                    <div style={{ maxHeight: "70vh", overflowY: "auto", overflowX: "auto" }}>
                        <table className="table align-middle table-hover table-nowrap mb-0 table-dark">
                            <thead className="table-success" style={{
                                position: "sticky",
                                top: 0,
                                zIndex: 5,
                                backgroundColor: "#3C3C3C",
                                boxShadow: "0 2px 4px rgba(0,0,0,0.05)"
                            }}>
                                <tr>
                                    <th>Logo</th>
                                    <th>Location</th>
                                    <th>Status</th>
                                    <th>Manager</th>
                                    <th>Invoicing</th>
                                    <th>Operating Structure</th>
                                    <th>CurbAds?</th>
                                    <th>Monthly Parking?</th>
                                </tr>
                            </thead>
                            <tbody>
                                {filtered
                                    .sort((a, b) => a.location_name.localeCompare(b.location_name))
                                    .map((lot, idx) => (
                                        <tr
                                            key={idx}
                                            onClick={() => handleRowClick(lot.id)}
                                            style={{ cursor: "pointer" }}
                                        >
                                            <td><img
                                                src={lot.logo_files?.length > 0 ? `${FILES_URL}${lot.logo_files[0].file_name}` : 'https://placehold.co/150x150'}
                                                alt="Client Logo"
                                                style={{
                                                    maxHeight: '50px',
                                                    marginRight: '15px',
                                                    maxWidth: '150px'
                                                }}
                                                onError={(e) => {
                                                    e.target.onerror = null;
                                                    e.target.src = 'https://placehold.co/150x150';
                                                }}
                                            /></td>
                                            <td>{lot.location_name}</td>
                                            <td>
                                                <span className="badge" style={{ background: "#FFCA5C", fontWeight: "bold", color: "#000000" }}>
                                                    {lot.is_active == 1 ? "Active" : "Inactive"}
                                                </span>
                                            </td>
                                            <td>{lot.account_manager.full_name}</td>
                                            <td>
                                                <span className="badge bg-info" style={{ fontWeight: "bold", fontSize: 10, padding: 5, color: "#FFFFFF", marginLeft: 10 }}>
                                                    {lot.invoicing_type}
                                                </span>
                                            </td>
                                            <td>
                                                <span className="badge bg-danger" style={{ fontWeight: "bold", fontSize: 10, padding: 5, color: "#FFFFFF", marginLeft: 10 }}>
                                                    {lot.operating_structure}
                                                </span>
                                            </td>
                                            <td>
                                                {lot.is_curbads_location == 1 &&
                                                    <span className="badge" style={{ background: "#5cd9ffff", fontWeight: "bold", color: "#ffffffff" }}>CurbAds</span>
                                                }
                                            </td>
                                            <td>
                                                {lot.is_monthly_parking_active == 1 &&
                                                    <span className="badge bg-dark" style={{ fontWeight: "bold", fontSize: 10, padding: 5, color: "#FFFFFF", marginLeft: 10 }}>
                                                        YES
                                                    </span>
                                                }
                                            </td>
                                        </tr>
                                    ))}
                            </tbody>
                        </table>
                    </div>
                </CardBody>
            </Card>
        </Col>
    )
}

LocationsTable.propTypes = {
    title: PropTypes.string,
    lots: PropTypes.arrayOf(
        PropTypes.shape({
            id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
            name: PropTypes.string,
            status: PropTypes.string,
            supervisor: PropTypes.string,
            staff: PropTypes.number,
            carsInLot: PropTypes.number,
            totalCars: PropTypes.number,
            total: PropTypes.string
        })
    )
}

export default LocationsTable